import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { SkeletonRows, ErrorMessage, usePageMeta } from '../components/ui';
import AdSlot from '../components/AdSlot';
import { RecentTags, rememberTag } from '../components/RecentTags';

const pct = (x) => (x == null ? '—' : `${(x * 100).toFixed(1)}%`);

/* Supercell's public deck link: opens the game with the deck ready to copy. */
export function copyLink(ids) {
  return `https://link.clashroyale.com/en/?clashroyale://copyDeck?deck=${ids.join(';')}&l=Royals`;
}

/* Slot order the game expects: evolution picks first, then the hero, then the
   rest, so the copied deck lands with its special forms in the right slots. */
export function slotOrder(deck) {
  const front = [...(deck.evo || []).slice(0, 1), ...(deck.hero || []), ...(deck.evo || []).slice(1)];
  const seen = new Set();
  return [...front, ...deck.cards].filter((c) => deck.cards.includes(c) && !seen.has(c) && seen.add(c));
}

/* Which art a card shows: its evolution or hero form when the deck plays it
   that way and the API has that picture, else the plain card. */
export function formOf(deck, id) {
  if ((deck.hero || []).includes(id)) return 'hero';
  if ((deck.evo || []).includes(id)) return 'evo';
  return null;
}

export function CardTile({ id, card, level, cap, form }) {
  const [broken, setBroken] = useState(false);
  const formIcon = form === 'hero' ? card?.hero_icon : form === 'evo' ? card?.evo_icon : null;
  const icon = formIcon || card?.icon;
  const base = card?.name || `#${id}`;
  const name = formIcon ? `${form === 'hero' ? 'Hero' : 'Evolved'} ${base}` : base;
  const maxed = level != null && level >= cap;
  return (
    <div className={`dk-card${level != null && !maxed ? ' under' : ''}`} title={level != null ? `${name} · level ${level}` : name}>
      {icon && !broken ? (
        <img src={icon} alt={name} loading="lazy" onError={() => setBroken(true)} />
      ) : (
        <span className="dk-fb">{name.slice(0, 3)}</span>
      )}
      {level != null && <span className={`dk-lv${maxed ? ' max' : ''}`}>{maxed ? 'MAX' : level}</span>}
    </div>
  );
}

function DeckCard({ deck, cards, cap }) {
  const order = slotOrder(deck);
  const levelOf = deck.levels ? Object.fromEntries(deck.cards.map((c, i) => [c, deck.levels[i]])) : {};
  return (
    <div className="dk">
      <div className="dk-head">
        <div>
          <div className="dk-name">{deck.archetype}</div>
          <div className="pt">
            {deck.elixir != null ? `${deck.elixir} avg elixir · ` : ''}{deck.n.toLocaleString()} games
            {deck.maxed != null ? ` · ${deck.maxed}/8 of yours maxed` : ''}
          </div>
        </div>
        <div className="dk-wr">
          <div className="v">{pct(deck.adj_win_rate)}</div>
          <div className="l">win rate</div>
        </div>
      </div>
      <div className="dk-grid">
        {order.map((c) => <CardTile key={c} id={c} card={cards[String(c)]} level={levelOf[c]} cap={cap} form={formOf(deck, c)} />)}
      </div>
      {deck.plan && (
        <div className="dk-plan">
          <div className="dk-vars-h">How to play it · {deck.plan.style}</div>
          <p>{deck.plan.text}</p>
        </div>
      )}
      <div className="dk-foot">
        <a className="dk-copy" href={copyLink(order)} target="_blank" rel="noopener noreferrer">Copy to game ↗</a>
      </div>
    </div>
  );
}

function Section({ title, sub, decks, data }) {
  return (
    <>
      <div className="subhead">{title}</div>
      {sub && <div className="sub dk-sub">{sub}</div>}
      {decks.length === 0 ? (
        <div className="empty-state">No deck in this week's catalogue fits here yet.</div>
      ) : (
        <div className="dk-list">
          {decks.map((d) => <DeckCard key={d.cards.join('-')} deck={d} cards={data.cards} cap={data.level_cap} />)}
        </div>
      )}
    </>
  );
}

/* River Race war sets: four decks with no card in common. The first is the
   best combined set; the others each leave out one of its win conditions. */
function WarSets({ sets, data }) {
  const [pick, setPick] = useState(0);
  const cur = sets[Math.min(pick, sets.length - 1)];
  return (
    <>
      <div className="subhead">War decks: 4 decks, no shared cards</div>
      <div className="sub dk-sub">
        A River Race day lets you use each card once across four decks. These sets have the best combined
        win rate at your levels; switch sets if you'd rather not play one of the win conditions.
      </div>
      {!cur ? (
        <div className="empty-state">No deck in this week's catalogue fits here yet.</div>
      ) : (
        <>
          {sets.length > 1 && (
            <div className="filters">
              <div className="seg">
                {sets.map((s, i) => (
                  <button key={s.label} type="button" className={s === cur ? 'on' : ''} onClick={() => setPick(i)}>
                    {s.label} · {pct(s.avg_win_rate)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {cur.decks.length < 4 && (
            <div className="sub dk-sub">Only {cur.decks.length} decks without shared cards fit your collection this week.</div>
          )}
          <div className="dk-list">
            {cur.decks.map((d) => <DeckCard key={d.cards.join('-')} deck={d} cards={data.cards} cap={data.level_cap} />)}
          </div>
        </>
      )}
    </>
  );
}

const nameOf = (cards, id) => cards[String(id)]?.name || `#${id}`;

/* One of the player's own recent decks: its record from the battlelog, and
   one- or two-card swaps that elite players win more with. */
function RecentDeck({ deck, data }) {
  const { cards } = data;
  const total = deck.wins + deck.losses + deck.draws;
  const modes = Object.entries(deck.modes).map(([m, n]) => `${m} ${n}`).join(' · ');
  return (
    <div className="dk">
      <div className="dk-head">
        <div>
          <div className="dk-name">{deck.wins}W {deck.losses}L{deck.draws ? ` ${deck.draws}D` : ''}</div>
          <div className="pt">{modes}</div>
        </div>
        <div className="dk-wr">
          <div className="v">{total ? `${Math.round((deck.wins / total) * 100)}%` : '—'}</div>
          <div className="l">your wins</div>
        </div>
      </div>
      <div className="dk-grid">
        {deck.cards.map((c) => <CardTile key={c} id={c} card={cards[String(c)]} form={formOf(deck, c)} />)}
      </div>
      {deck.elite_adj_win_rate != null && (
        <div className="pt">Elite players win {pct(deck.elite_adj_win_rate)} with this exact deck ({deck.elite_n.toLocaleString()} games).</div>
      )}
      {deck.variations.length > 0 ? (
        <div className="dk-vars">
          <div className="dk-vars-h">What elite players run instead</div>
          {deck.variations.map((v) => (
            <div key={v.cards.join('-')} className="dk-var">
              <div>
                <div className="dk-swap">
                  {v.out.map((c) => nameOf(cards, c)).join(' + ')} → {v.in.map((c) => nameOf(cards, c)).join(' + ')}
                </div>
                <div className="pt">
                  {pct(v.elite_adj_win_rate)} over {v.elite_n.toLocaleString()} elite games
                  {v.missing.length > 0 ? ` · you don't have ${v.missing.map((c) => nameOf(cards, c)).join(', ')}` : ''}
                  {v.missing.length === 0 && v.in_levels.some((lv) => lv != null && lv < data.cap)
                    ? ` · yours at level ${v.in_levels.join(', ')}` : ''}
                </div>
              </div>
              <a className="dk-copy" href={copyLink(slotOrder(v))} target="_blank" rel="noopener noreferrer">Copy ↗</a>
            </div>
          ))}
        </div>
      ) : (
        <div className="pt">No close variation with enough elite games this week.</div>
      )}
    </div>
  );
}

/* Deck ideas: this week's strongest decks for anyone, and for a player tag,
   decks picked around the cards they have maxed, plus four card-disjoint
   decks for a River Race day. Built weekly from tracked battles. */
function ClashDecks() {
  const { tag } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(tag || '');
  // Results are kept with the tag they were loaded for. Switching from the
  // weekly list to a tag renders once before the fetch effect runs, and that
  // render must not treat the weekly list as a player's results.
  const [result, setResult] = useState({ tag: null, data: null, error: null });
  const [nonce, setNonce] = useState(0);
  const key = tag || '';
  const data = result.tag === key ? result.data : null;
  const error = result.tag === key ? result.error : null;
  usePageMeta({
    title: data?.player?.name ? `${data.player.name} · Deck Ideas` : 'Best Clash Royale Decks for Your Cards',
    description: 'The strongest Clash Royale decks this week, and deck ideas built around the cards you have maxed: meta picks, off-meta fun decks and four-deck war sets with game plans.',
    noindex: !!tag,
  });

  useEffect(() => { setInput(tag || ''); }, [tag]);

  useEffect(() => {
    let cancelled = false;
    const key = tag || '';
    setResult({ tag: null, data: null, error: null });
    (tag ? api.getClashPlayerDecks(tag) : api.getClashDecks())
      .then((d) => {
        if (cancelled) return;
        setResult({ tag: key, data: d, error: null });
        if (tag) rememberTag('clash', tag, d?.player?.name);
      })
      .catch((err) => { if (!cancelled) setResult({ tag: key, data: null, error: err.message }); });
    return () => { cancelled = true; };
  }, [tag, nonce]);

  function submit(e) {
    e.preventDefault();
    // Supercell tags never use the letter O, so a typed O is a zero.
    const t = input.trim().replace(/^#/, '').toUpperCase().replace(/O/g, '0');
    if (t) navigate(`/clash/decks/${encodeURIComponent(t)}`);
  }

  const total = data ? Object.values(data.battles || {}).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="page">
      <div className="wrap">
        <div className="phead">
          <div>
            <div className="eyebrow">Clash Royale · updated weekly</div>
            <h1>Deck Ideas for Your Cards</h1>
            <div className="sub">
              Enter your tag to see your record with your recent decks and the swaps elite players make, plus decks picked around the cards you've maxed: the strongest ones at your levels,
              some off-meta decks worth a try, and sets of four war decks with no shared cards, each with a short game plan.
              {data ? ` Win rates come from ${total.toLocaleString()} battles over the last ${data.window_days} days.` : ''}
            </div>
          </div>
        </div>

        <form className="filters" onSubmit={submit}>
          <input
            className="search-input"
            placeholder="Your player tag, e.g. #2PP"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            aria-label="Clash Royale player tag"
          />
          <button type="submit" className="retry-btn">Find my decks</button>
        </form>
        <RecentTags game="clash" current={tag} onPick={(t) => navigate(`/clash/decks/${encodeURIComponent(t)}`)} />

        {error ? (
          <ErrorMessage message={error} onRetry={() => setNonce((n) => n + 1)} />
        ) : !data ? (
          <SkeletonRows rows={6} />
        ) : !tag ? (
          <Section
            title="Strongest decks this week"
            sub={`Ranked by win rate after taking out card levels, with decks on few games pulled toward 50%. Only decks with ${data.min_plays}+ games count.`}
            decks={data.decks}
            data={data}
          />
        ) : (
          <>
            <div className="phead" style={{ marginTop: 6 }}>
              <div>
                <h2 style={{ margin: 0 }}>{data.player.name}</h2>
                <div className="sub">{data.player.tag} · {data.player.trophies?.toLocaleString() ?? '—'} trophies</div>
              </div>
            </div>

            <div className="stat-ribbon">
              <div className="srib"><div className="v">{data.maxed_cards.length}</div><div className="l">Cards at level {data.cap}</div></div>
              <div className="srib"><div className="v">{data.eligible_decks.toLocaleString()}</div><div className="l">Decks you can build</div></div>
              <div className="srib"><div className="v">{data.current ? pct(data.current.adj_win_rate) : '—'}</div><div className="l">Your current deck</div></div>
              <div className="srib"><div className="v">{data.generated_at.slice(0, 10)}</div><div className="l">Last refreshed</div></div>
            </div>

            {data.recent?.length > 0 && (
              <>
                <div className="subhead">Your recent decks</div>
                <div className="sub dk-sub">
                  Your record with each deck over your last {data.recent.reduce((a, d) => a + d.games, 0)} games
                  (the API keeps about 30), and swaps that Path of Legends and {data.elite_trophies?.toLocaleString() ?? '10,000'}+
                  trophy players are winning with.
                </div>
                <div className="dk-list">
                  {data.recent.map((d) => <RecentDeck key={d.cards.join('-')} deck={d} data={data} />)}
                </div>
              </>
            )}

            <Section
              title="Best at your levels"
              sub="The strongest decks once your card levels are counted: every level a card is below max costs win rate, so decks on your maxed cards rise. One per win condition."
              decks={data.best}
              data={data}
            />
            <AdSlot />
            <Section
              title="Fun to try"
              sub="Off-meta decks you can build that still win at least half their games. Fewer people know how to play against them."
              decks={data.fun}
              data={data}
            />
            <WarSets sets={data.war_sets || [{ label: 'Best combined', decks: data.war }]} data={data} />

            <div className="tip">
              <b>How this works.</b> The Clash Royale API doesn't list decks, so we build the list from tracked
              Trophy Road, Path of Legends and war battles, then match it against your card levels from the official
              API. A card shown with a number isn't maxed yet; we count each missing level against the deck, using the
              same level effect as the <Link to={`/clash/${encodeURIComponent(tag)}`}>upgrade advisor</Link>.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ClashDecks;
